import { Link } from 'react-router-dom';
import { LikeIcon, ClockIcon, PeopleIcon, ChecklistIcon } from '../activity-details/icons';
import type { BookmarkedActivity } from '../../types/bookmark.types';
import deleteIconSvg from '../../../assets/icons/delete-icon.svg';
import { mockActivityDetails } from '../activity-details';

interface LibraryActivityCardProps {
  bookmark?: BookmarkedActivity;
  activity?: {
    id: string | number;
    title: string;
    imageUrl?: string;
    likesCount: number;
    duration: string;
    durationText?: string;
    playType: string;
    playTypeText?: string;
    prepNeeded: string;
    prepNeededText?: string;
  };
  onDelete?: (bookmarkId: number) => void;
  isDeleting?: boolean;
  onClick?: () => void
}
/**
 * Card component for displaying bookmarked activities in library
 * Supports click to navigate and delete button
 */
export default function LibraryActivityCard({
  bookmark,
  onDelete,
  isDeleting = false,
  ...rest
}: LibraryActivityCardProps) {
  const activity = bookmark?.activity ?? rest.activity;

  const imageUrl = activity?.imageUrl ?? mockActivityDetails.imageUrl

  const handleDelete = (e: React.MouseEvent) => {
    // Prevent navigation when clicking delete button
    e.preventDefault();
    e.stopPropagation();
    bookmark?.id && onDelete?.(bookmark.id);
  };

  return (
    <div>
      <Link
        to={`/activity/${activity && activity.id}`}
        className={`
          flex gap-[10px] sm:gap-[12px] md:gap-[14px] 
          hover:opacity-80 transition-opacity
          ${isDeleting ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        {/* Activity Thumbnail with Like Badge */}
        <div className="relative flex-shrink-0 self-start">
          <img
            src={imageUrl}
            alt={activity && activity.title}
            className="w-[124px] h-[93px] sm:w-[130px] sm:h-[98px] md:w-[136px] md:h-[102px] lg:w-[140px] lg:h-[105px] object-cover rounded-lg"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = '/placeholder-activity.jpg';
            }}
          />
          {/* Like Count Badge - only show if likesCount exists in activity data */}
          {activity?.likesCount !== undefined && activity.likesCount > 0 && (
            <div
              className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
            >
              <LikeIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
              <span className="text-xs sm:text-sm font-medium text-white">
                {activity.likesCount}
              </span>
            </div>
          )}
        </div>

        {/* Activity Details */}
        <div className="flex-1 flex flex-col justify-center gap-2 sm:gap-2.5 md:gap-3 min-w-0">
          <h3
            className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-[#1B2831] truncate"
            style={{ letterSpacing: '-0.72px' }}
          >
            {activity?.title}
          </h3>

          <div className="flex items-center gap-3 sm:gap-3.5 md:gap-4 flex-wrap">
            {activity?.durationText && (
              <div className="flex items-center gap-1">
                <ClockIcon className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#1B2831]" />
                <span className="text-[12px] sm:text-[13px] md:text-[14px] font-medium text-[#1B2831]">
                  {activity.durationText}
                </span>
              </div>
            )}

            {activity?.playTypeText && (
              <div className="flex items-center gap-1">
                <PeopleIcon className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#1B2831]" />
                <span className="text-[12px] sm:text-[13px] md:text-[14px] font-medium text-[#1B2831]">
                  {activity.playTypeText}
                </span>
              </div>
            )}

            {activity?.prepNeededText && (
              <div className="flex items-center gap-1">
                <ChecklistIcon className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#1B2831]" />
                <span className="text-[12px] sm:text-[13px] md:text-[14px] font-medium text-[#1B2831]">
                  {activity.prepNeededText}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Delete Button */}
        <div className="flex items-center">
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            aria-label="Remove from library"
            title="Remove from library"
          >
            {isDeleting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <img
                src={deleteIconSvg}
                alt="Delete"
                className="w-5 h-5"
                style={{
                  filter:
                    'brightness(0) saturate(100%) invert(34%) sepia(68%) saturate(3595%) hue-rotate(346deg) brightness(97%) contrast(90%)',
                }}
              />
            )}
          </button>
        </div>
      </Link>

      {/* Divider - Responsive spacing */}
      <div className="h-[1px] bg-[#E5E5E5] mt-4 mb-4 sm:mt-5 sm:mb-5 md:mt-6 md:mb-6" />
    </div>
  );
}
